import "reflect-metadata";
import React from "react";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import ReactDOM from "react-dom/client";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { container, Injector, SERVICES_TYPES } from "@/ioc";
import { AppStore } from "@/store/root";
import { ThemeProvider } from "@/hooks/useTheme";
import App from "./App";

import "./index.css";
import { Toaster } from "./components/ui/toaster";
import { ToastProvider } from "./components/ui/toast";

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
const store = container.get<AppStore>(SERVICES_TYPES.STORE);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 3;
      }
    },
    mutations: {
      retry: 1
    }
  }
});

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Injector container={container}>
        <Provider store={store}>
          <BrowserRouter basename="/">
            <ThemeProvider defaultTheme="system" storageKey="ui-theme">
              <ToastProvider>
                <Toaster />
                <App />
              </ToastProvider>
            </ThemeProvider>
          </BrowserRouter>
        </Provider>
      </Injector>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
    </QueryClientProvider>
  </React.StrictMode>
);
